import { css } from "@emotion/react";
import {
  fontSize,
  fontWeight,
  letterSpacing,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  accentColor,
  borderColor,
  shadeColor,
  shadowColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import {
  lessThanBreakpoint,
  moreThanBreakpoint,
} from "main/javascripts/styles/base/responsiveStyle";
import ImgPlus from "../../../images/lexical/icons/plus.svg";
import { themeStyleTabiChat } from "./ThemeStyleTabiChat";

const overwriteThemeStyle = process.env.HOSTING_DOMAIN
  ? undefined
  : themeStyleTabiChat;

// Editor/Postの両方で使用している
export const themeStyle = css`
  h2,
  h3,
  h4 {
    :target {
      padding-top: 48px;
      margin-top: -48px;
    }
  }
  .editor__ltr {
    text-align: left;
  }
  .editor__rtl {
    text-align: right;
  }
  .editor__paragraph {
    position: relative;
    font-size: ${fontSize.body};
    line-height: 2;
    letter-spacing: 0.05em;
    color: ${textColor.secondaryDarkColor};
    margin-bottom: ${space.atom3x};
    padding: calc(
        ${space.atom} - (${lineHeight.mediumHeadline1} - ${fontSize.body}) / 2
      )
      ${space.atom};
    ${moreThanBreakpoint("desktop")} {
      font-size: ${fontSize.mediumHeading};
    }
  }
  //.editor__paragraph:last-child {
  //  margin-bottom: 0;
  //}
  .editor__quote {
    font-size: ${fontSize.body};
    line-height: 2;
    letter-spacing: 0.05em;
    color: ${textColor.secondaryDarkColor};
    margin: 0 ${space.atom} ${space.atom3x} ${space.atom};
    border-left: 0.375rem solid #eee;
    padding: ${space.atom} 0 ${space.atom} ${space.atom3x};
    ${moreThanBreakpoint("desktop")} {
      font-size: ${fontSize.mediumHeading};
    }
  }
  .editor__h1 {
    font-size: 24px;
    color: rgb(5, 5, 5);
    font-weight: 400;
    margin: 0;
    margin-bottom: 12px;
    padding: 0;
  }
  .editor__h2 {
    font-size: ${fontSize.headline};
    line-height: ${lineHeight.headline1};
    font-weight: ${fontWeight.bold};
    margin-top: ${space.atom4x};
    margin-bottom: ${space.atom3x};
    letter-spacing: 0.05em;
    padding: calc(
        ${space.atom} - (${lineHeight.headline1} - ${fontSize.headline}) / 2
      )
      ${space.atom};
    &:after {
      content: "";
      width: 4.5rem;
      height: 0.1875rem;
      display: block;
      background-color: ${accentColor.primaryColor};
      margin: ${space.atom2x} 0 ${space.atom};
    }
    ${moreThanBreakpoint("tablet")} {
      font-size: ${fontSize.title};
      line-height: ${lineHeight.title};
      padding: calc(
          ${space.atom} - (${lineHeight.title} - ${fontSize.title}) / 2
        )
        ${space.atom};
    }
  }
  .editor__h3 {
    display: flex;
    font-size: ${fontSize.mediumHeading};
    line-height: ${lineHeight.mediumHeadline1};
    font-weight: ${fontWeight.bold};
    margin-top: ${space.atom4x};
    margin-bottom: ${space.atom3x};
    letter-spacing: 0.05em;
    padding: calc(
        ${space.atom} -
          (${lineHeight.mediumHeadline1} - ${fontSize.mediumHeading}) / 2
      )
      ${space.atom};
    &:before {
      content: "";
      width: 0.1875rem;
      min-width: 0.1875rem;
      display: block;
      background-color: ${accentColor.primaryColor};
      margin: 0 ${space.atom2x} 0 0;
    }
    ${moreThanBreakpoint("tablet")} {
      font-size: ${fontSize.headline};
      line-height: ${lineHeight.headline1};
      padding: calc(
          ${space.atom} - (${lineHeight.headline1} - ${fontSize.headline}) / 2
        )
        ${space.atom};
    }
  }
  .editor__h4 {
    font-size: ${fontSize.subHeading};
    line-height: ${lineHeight.subHeadline1};
    font-weight: ${fontWeight.bold};
    margin-bottom: ${space.atom3x};
    letter-spacing: 0.05em;
    padding: calc(
        ${space.atom} - (${lineHeight.subHeadline2} - ${fontSize.subHeading}) /
          2
      )
      ${space.atom};
    ${moreThanBreakpoint("tablet")} {
      font-size: ${fontSize.mediumHeading};
      line-height: ${lineHeight.mediumHeadline1};
      padding: calc(
          ${space.atom} -
            (${lineHeight.mediumHeadline1} - ${fontSize.mediumHeading}) / 2
        )
        ${space.atom};
    }
  }
  .editor__indent {
    --lexical-indent-base-value: 2.5rem;
  }
  .editor__textBold {
    font-weight: bold;
  }
  .editor__textItalic {
    font-style: italic;
  }
  .editor__textUnderline {
    text-decoration: underline;
  }
  .editor__textStrikethrough {
    text-decoration: line-through;
  }
  .editor__textUnderlineStrikethrough {
    text-decoration: underline line-through;
  }
  .editor__textSubscript {
    font-size: 0.8em;
    vertical-align: sub !important;
  }
  .editor__textSuperscript {
    font-size: 0.8em;
    vertical-align: super;
  }
  .editor__textCode {
    background-color: rgb(240, 242, 245);
    padding: 1px 0.25rem;
    font-family: Menlo, Consolas, Monaco, monospace;
    font-size: 94%;
  }
  .editor__hashtag {
    background-color: rgba(88, 144, 255, 0.15);
    border-bottom: 1px solid rgba(88, 144, 255, 0.3);
  }
  .editor__link {
    color: ${accentColor.secondaryColor};
    text-decoration: underline;
  }
  .editor__link:hover {
    text-decoration: underline;
  }
  .editor__code {
    background-color: rgb(240, 242, 245);
    font-family: Menlo, Consolas, Monaco, monospace;
    display: block;
    padding: 8px 8px 8px 52px;
    line-height: 1.53;
    font-size: 13px;
    margin: 0 ${space.atom} ${space.atom3x};
    tab-size: 2;
    /* white-space: pre; */
    overflow-x: auto;
    position: relative;
  }
  .editor__code:before {
    content: attr(data-gutter);
    position: absolute;
    background-color: #eee;
    left: 0;
    top: 0;
    border-right: 1px solid #ccc;
    padding: 8px;
    color: #777;
    white-space: pre-wrap;
    text-align: right;
    min-width: 25px;
  }
  .editor__tableBlock {
    position: relative;
    width: calc(100% - ${space.atom2x});
    margin: 0 ${space.atom} ${space.atom4x};
    box-sizing: border-box;
    table {
      margin: 0;
      width: 100%;
    }
  }
  .editor__tableBlockScrollable {
    .editor__tableBlockInner {
      overflow-x: auto;
    }
    ${lessThanBreakpoint("tablet")} {
      &:before,
      &:after {
        content: "";
        position: absolute;
        top: 0;
        width: 2rem;
        height: 100%;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s;
      }
      &:before {
        left: 0;
        background: linear-gradient(
          90deg,
          rgba(0, 0, 0, 0.08),
          rgba(0, 0, 0, 0)
        );
      }
      &:after {
        right: 0;
        background: linear-gradient(
          270deg,
          rgba(0, 0, 0, 0.08),
          rgba(0, 0, 0, 0)
        );
      }
      &.scroll--start {
        &:before {
          opacity: 1;
        }
      }
      &.scroll--end {
        &:after {
          opacity: 1;
        }
      }
    }
  }
  .editor__table {
    border-collapse: collapse;
    border-spacing: 0;
    max-width: 100%;
    overflow-y: scroll;
    table-layout: fixed;
    width: calc(100% - ${space.atom2x});
    margin: 0 ${space.atom} ${space.atom4x};
  }
  .editor__tableScrollable {
    ${lessThanBreakpoint("tablet")} {
      width: max-content !important;
      max-width: initial;
      margin: 0;
      th,
      td {
        width: calc(
          (100vw - var(--scrollbar-width) - (${space.atom2x} * 8)) / 2.5
        ) !important;
      }
    }
    ${lessThanBreakpoint("phone")} {
      th,
      td {
        width: calc(
          (100vw - var(--scrollbar-width) - (${space.atom2x} * 2)) / 2.5
        ) !important;
      }
    }
  }
  .editor__tableSelection *::selection {
    background-color: transparent;
  }
  .editor__tableSelected {
    outline: 2px solid rgb(60, 132, 244);
  }
  .editor__tableCell {
    border: 1px solid ${borderColor.secondaryLightColor} !important;
    padding: ${space.atom0_5x} ${space.atom1_5x};
    vertical-align: top;
    text-align: start;
    box-sizing: border-box;
    width: auto !important;
    line-height: 2; // rowを追加した際にpタグが含まれないため暫定的に設定
    word-break: break-word;
    .editor__paragraph {
      margin-bottom: 0;
      padding: 0;
    }
  }
  .editor__tableCellSortedIndicator {
    display: block;
    opacity: 0.5;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background-color: #999;
  }
  .editor__tableCellResizer {
    position: absolute;
    right: -4px;
    height: 100%;
    width: 8px;
    cursor: ew-resize;
    z-index: 10;
    top: 0;
  }
  .editor__tableCellHeader {
    background-color: #f2f3f5; // 現状lexicalがhtml生成で色を固定しているようで変更できない
    text-align: start;
  }
  .editor__tableCellSelected {
    background-color: #c9dbf0;
  }
  .editor__tableCellPrimarySelected {
    border: 2px solid rgb(60, 132, 244);
    display: block;
    height: calc(100% - 2px);
    position: absolute;
    width: calc(100% - 2px);
    left: -1px;
    top: -1px;
    z-index: 2;
  }
  .editor__tableCellEditing {
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.4);
    border-radius: 3px;
  }
  .editor__tableAddColumns {
    position: absolute;
    top: 0;
    width: 20px;
    background-color: #eee;
    height: 100%;
    right: 0;
    animation: table-controls 0.2s ease;
    border: 0;
    cursor: pointer;
  }
  .editor__tableAddColumns:after {
    background-image: url(${ImgPlus});
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    display: block;
    content: " ";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.4;
  }
  .editor__tableAddColumns:hover {
    background-color: #c9dbf0;
  }
  .editor__tableAddRows {
    position: absolute;
    bottom: -25px;
    width: calc(100% - 25px);
    background-color: #eee;
    height: 20px;
    left: 0;
    animation: table-controls 0.2s ease;
    border: 0;
    cursor: pointer;
  }
  .editor__tableAddRows:after {
    background-image: url(${ImgPlus});
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    display: block;
    content: " ";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.4;
  }
  .editor__tableAddRows:hover {
    background-color: #c9dbf0;
  }
  @keyframes table-controls {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  .editor__tableCellResizeRuler {
    display: block;
    position: absolute;
    width: 1px;
    background-color: rgb(60, 132, 244);
    height: 100%;
    top: 0;
  }
  .editor__tableCellActionButtonContainer {
    display: block;
    right: 5px;
    top: 6px;
    position: absolute;
    z-index: 4;
    width: 20px;
    height: 20px;
  }
  .editor__tableCellActionButton {
    background-color: #eee;
    display: block;
    border: 0;
    border-radius: 20px;
    width: 20px;
    height: 20px;
    color: #222;
    cursor: pointer;
  }
  .editor__tableCellActionButton:hover {
    background-color: #ddd;
  }
  .editor__characterLimit {
    display: inline;
    background-color: #ffbbbb !important;
  }
  .editor__ol1 {
    margin: 0 0 ${space.atom3x};
    padding: 0 0.5rem 0 1.5rem;
  }
  .editor__ol2 {
    padding: 0;
    margin: 0;
    margin-left: 16px;
    list-style-type: upper-alpha;
    list-style-position: inside;
  }
  .editor__ol3 {
    padding: 0;
    margin: 0;
    margin-left: 16px;
    list-style-type: lower-alpha;
    list-style-position: inside;
  }
  .editor__ol4 {
    padding: 0;
    margin: 0;
    margin-left: 16px;
    list-style-type: upper-roman;
    list-style-position: inside;
  }
  .editor__ol5 {
    padding: 0;
    margin: 0;
    margin-left: 16px;
    list-style-type: lower-roman;
    list-style-position: inside;
  }
  .editor__ul {
    margin: 0 0 ${space.atom3x};
    padding: 0 0.5rem 0 1.5rem;
  }
  .editor__listItem {
    font-size: ${fontSize.body};
    line-height: ${lineHeight.body2};
    letter-spacing: 0.05em;
    color: ${textColor.secondaryDarkColor};
    margin-bottom: ${space.atom};
    margin-left: ${space.atom};
    padding: calc(${space.atom} - (${lineHeight.body2} - ${fontSize.body}) / 2)
      ${space.atom};
    ${moreThanBreakpoint("desktop")} {
      font-size: ${fontSize.mediumHeading};
    }
  }
  .editor__listItemChecked,
  .editor__listItemUnchecked {
    position: relative;
    margin-left: 8px;
    margin-right: 8px;
    padding-left: 24px;
    padding-right: 24px;
    list-style-type: none;
    outline: none;
  }
  .editor__listItemChecked {
    text-decoration: line-through;
  }
  .editor__listItemUnchecked:before,
  .editor__listItemChecked:before {
    content: "";
    width: 16px;
    height: 16px;
    top: 2px;
    left: 0;
    cursor: pointer;
    display: block;
    background-size: cover;
    position: absolute;
  }
  .editor__listItemUnchecked[dir="rtl"]:before,
  .editor__listItemChecked[dir="rtl"]:before {
    left: auto;
    right: 0;
  }
  .editor__listItemUnchecked:focus:before,
  .editor__listItemChecked:focus:before {
    box-shadow: 0 0 0 2px #a6cdfe;
    border-radius: 2px;
  }
  .editor__listItemUnchecked:before {
    border: 1px solid #999;
    border-radius: 2px;
  }
  .editor__listItemChecked:before {
    border: 1px solid rgb(61, 135, 245);
    border-radius: 2px;
    background-color: #3d87f5;
    background-repeat: no-repeat;
  }
  .editor__listItemChecked:after {
    content: "";
    cursor: pointer;
    border-color: #fff;
    border-style: solid;
    position: absolute;
    display: block;
    top: 6px;
    width: 3px;
    left: 7px;
    height: 6px;
    transform: rotate(45deg);
    border-width: 0 2px 2px 0;
  }
  .editor__nestedListItem {
    list-style-type: none;
  }
  .editor__nestedListItem:before,
  .editor__nestedListItem:after {
    display: none;
  }
  .editor__tokenComment {
    color: slategray;
  }
  .editor__tokenPunctuation {
    color: #999;
  }
  .editor__tokenProperty {
    color: #905;
  }
  .editor__tokenSelector {
    color: #690;
  }
  .editor__tokenOperator {
    color: #9a6e3a;
  }
  .editor__tokenAttr {
    color: #07a;
  }
  .editor__tokenVariable {
    color: #e90;
  }
  .editor__tokenFunction {
    color: #dd4a68;
  }
  .editor__mark {
    background: rgba(255, 212, 0, 0.14);
    border-bottom: 2px solid rgba(255, 212, 0, 0.3);
    padding-bottom: 2px;
  }
  .editor__markOverlap {
    background: rgba(255, 212, 0, 0.3);
    border-bottom: 2px solid rgba(255, 212, 0, 0.7);
  }
  .editor__mark.selected {
    background: rgba(255, 212, 0, 0.5);
    border-bottom: 2px solid rgba(255, 212, 0, 1);
  }
  .editor__markOverlap.selected {
    background: rgba(255, 212, 0, 0.7);
    border-bottom: 2px solid rgba(255, 212, 0, 0.7);
  }
  .editor__embedBlock {
    user-select: none;
  }
  .editor__embedBlockFocus {
    outline: 2px solid rgb(60, 132, 244);
  }
  .editor__layoutContaner {
    display: grid;
    gap: ${space.atom};
    margin: ${space.atom} 0;
  }
  .editor__layoutItem {
    border: 1px dashed #ddd;
    padding: ${space.atom} ${space.atom2x};
  }
  .editor__image {
    max-width: 100%;
    margin-bottom: ${space.atom3x};
    padding: ${space.atom2x} ${space.atom};
    img {
      box-sizing: border-box;
      max-width: 100%;
    }
    figcaption {
      padding-top: ${space.atom0_5x};
      a {
        color: ${textColor.disabledColor};
      }
      p {
        font-size: ${fontSize.caption};
        line-height: ${lineHeight.caption2};
        color: ${textColor.disabledColor};
        margin: 0;
        padding: 0;
        letter-spacing: 0;
      }
    }
  }
  .editor__instagram {
    padding: ${space.atom2x} ${space.atom};
    margin-bottom: ${space.atom3x};
    max-width: 100%;
    box-sizing: border-box;
    iframe {
      max-width: 100%;
      margin: 0 !important;
    }
  }
  .editor__tweet {
    padding: ${space.atom2x} ${space.atom};
    margin-bottom: ${space.atom3x};
    max-width: 100%;
    box-sizing: border-box;
    .twitter-tweet {
      margin: 0 !important;
      max-width: 542px !important;
    }
    iframe {
      max-width: 100%;
    }
  }
  .editor__youtube {
    width: 100%;
    padding: ${space.atom2x} ${space.atom};
    box-sizing: border-box;
    margin-bottom: ${space.atom3x};
    & > div {
      position: relative;
      padding-bottom: 56.25%;
      overflow: hidden;
    }
    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
  .editor__lineLink {
    width: fit-content;
    margin: 0 auto ${space.atom3x};
    padding: ${space.atom2x} ${space.atom};
    .promotion {
      text-align: center;
      font-size: ${fontSize.caption};
      font-weight: ${fontWeight.medium};
      margin-bottom: ${space.atom};
      max-width: 30rem;
      padding: 0 ${space.atom2x};
      .promotion-text {
        position: relative;
        display: inline-block;
        padding: 0 ${space.atom};
        letter-spacing: ${letterSpacing.text};
        &:before,
        &:after {
          content: "";
          position: absolute;
          background: ${textColor.primaryDarkColor};
          width: 2px;
          height: 24px;
        }
        &:before {
          left: -10px;
          bottom: -2px;
          transform-origin: left center;
          transform: rotate(-30deg);
        }
        &:after {
          right: -10px;
          bottom: -2px;
          transform-origin: right center;
          transform: rotate(30deg);
        }
      }
    }
    .button {
      display: flex;
      min-width: 17.5rem;
      max-width: 30rem;
      min-height: 3.125rem;
      text-decoration: none;
      margin: 0 auto;
      position: relative;
      .inner {
        display: flex;
        width: 100%;
        background: ${accentColor.lineColor};
        box-shadow: 0 4px 0 ${shadeColor(accentColor.lineColor, -20)};
        border-radius: 0.5rem;
        color: #fff;
        transition: background-color 0.3s ease, box-shadow 0.3s ease;
        &:hover {
          background-color: ${shadeColor(accentColor.lineColor, 5)};
          box-shadow: 0 4px 0 ${shadeColor(accentColor.lineColor, -10)};
        }
      }
    }
    .symbol {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      box-sizing: border-box;
      border-right: 1px solid rgba(0, 0, 0, 0.1);
      img {
        width: 26px;
      }
    }
    .text {
      font-size: ${fontSize.body};
      font-weight: ${fontWeight.medium};
      line-height: 1.4;
      padding: ${space.atom} ${space.atom2x};
      display: flex;
      align-items: center;
      flex: 1;
      letter-spacing: ${letterSpacing.text};
    }
  }
  .editor__searchForm {
    max-width: 500px;
    margin: 0 auto ${space.atom3x};
    padding: ${space.atom2x} ${space.atom};
    .mock {
    }
    .inner {
      border-radius: 8px;
      border: 1px solid ${accentColor.primaryColor};
      box-shadow: 0 0.125rem 1rem ${shadowColor.soft};
      overflow: hidden;
    }
    .category-tab {
      border-bottom: 1px solid ${accentColor.primaryColor};
      display: flex;
    }
    .tab {
      position: relative;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 0 0.1rem 1px;
      font-size: ${fontSize.smallest};
      text-align: center;
      height: 44px;
      color: ${accentColor.primaryColor};
      box-sizing: border-box;
      background-color: #fff;
      ${moreThanBreakpoint("tablet")} {
        padding: 0 1rem 1px;
        flex-direction: row;
        align-items: center;
        height: 38px;
        line-height: 41px;
        font-size: ${fontSize.caption};
      }
    }
    .form-block {
      padding: ${space.atom1_5x} ${space.atom2x};
      height: 2.5rem;
    }
  }
  hr {
    margin: ${space.atom2x};
    border-color: ${borderColor.secondaryLightColor};
  }
  ${overwriteThemeStyle}
`;
